import api from './api.service';
import { IAppointment, IPublicAppointment } from '@/types/appointment.types';

// Represents a single service line item in the appointment form
export interface ServiceLineItem {
  serviceId: string | null;
  employeeId: string | null;
  startTime: string;
  endTime: string;
  price: number;
}

// Represents the overall state of the appointment form
export interface AppointmentFormState {
  clientId: string | null;
  appointmentDate: string;
  serviceLineItems: ServiceLineItem[];
  notes?: string;
  offerId?: string;
  // Fields for public booking
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
}

// Data structure for backend (protected booking)
interface ServiceBookingPayload {
  serviceId: number;
  employeeId: string;
  startTime: string;
  endTime: string;
  priceOverride?: number;
}

interface CreateAppointmentPayload {
  clientId: string;
  appointmentDate: string;
  services: ServiceBookingPayload[];
  notes?: string;
  offerId?: string;
}


class AppointmentService {
  /**
   * Create appointment for authenticated users (admin/employee/customer)
   */
  async createAppointment(appointmentData: AppointmentFormState): Promise<IAppointment> {
    if (!appointmentData.clientId) {
      throw new Error("Client ID is missing.");
    }
    const payload: CreateAppointmentPayload = {
      clientId: appointmentData.clientId,
      appointmentDate: appointmentData.appointmentDate,
      notes: appointmentData.notes,
      offerId: appointmentData.offerId,
      services: appointmentData.serviceLineItems.map((item: ServiceLineItem) => {
        if (!item.serviceId || !item.employeeId) {
          throw new Error("Incomplete service line item data.");
        }
        return {
          serviceId: parseInt(item.serviceId, 10),
          employeeId: item.employeeId,
          startTime: item.startTime,
          endTime: item.endTime,
          priceOverride: item.price,
        };
      }),
    };
    const response = await api.post('/appointments', payload);
    return response.data;
  }

  /**
   * Create appointment for public/guest users
   */
  async createPublicAppointment(appointmentData: IPublicAppointment): Promise<any> {
    // Only send allowed fields for public booking
    const payload: IPublicAppointment = {
      customerName: appointmentData.customerName,
      customerEmail: appointmentData.customerEmail,
      customerPhone: appointmentData.customerPhone,
      serviceId: appointmentData.serviceId,
      employeeId: appointmentData.employeeId,
      appointmentDate: appointmentData.appointmentDate,
      appointmentTime: appointmentData.appointmentTime,
      notes: appointmentData.notes,
    };
    const response = await api.post('/appointments/public', payload);
    return response.data;
  }

  /**
   * Get appointment details by ID (authenticated)
   */
  async getAppointmentById(id: string): Promise<any> {
    const response = await api.get(`/appointments/${id}`);
    return response.data.data;
  }

  /**
   * Get appointment details for public booking confirmation
   */
  async getPublicAppointmentById(id: string, token?: string, phone?: string): Promise<any> {
    const params: Record<string, string> = {};
    if (token) params.token = token;
    if (phone) params.phone = phone;
    const response = await api.get(`/appointments/public/${id}`, { params });
    return response.data.data;
  }
}

const appointmentService = new AppointmentService();

/**
 * Helper for public appointment creation
 */
export const createPublicAppointment = (appointmentData: IPublicAppointment) => {
  return appointmentService.createPublicAppointment(appointmentData);
};

export default appointmentService;